import { useState } from 'react'
import { Button, Fa } from '@eltoro-ui/components'
import { usePagination } from '@eltoro-ui/hooks'
import { V1WebToHomeEvent } from 'next-gen-sdk'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { capitalize } from 'Helpers'
import { TablePagination } from 'Components/TablePagination'
import { useAppContext } from 'Contexts'

export const PixelSubscriptionHistory = ({
  type,
  history,
}: {
  type: 'pixel' | 'subscription'
  history: Array<V1WebToHomeEvent>
}) => {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const { isAdmin } = useAppContext()

  const sortedHistory = history.sort((a, b) => {
    const aEvent =
      a.pixelCreated ||
      a.pixelUpdated ||
      a.subscriptionCreated ||
      a.subscriptionUpdated
    const bEvent =
      b.pixelCreated ||
      b.pixelUpdated ||
      b.subscriptionCreated ||
      b.subscriptionUpdated
    if (!bEvent?.timestamp || !aEvent?.timestamp) return 0
    return (
      new Date(aEvent.timestamp).getTime() -
      new Date(bEvent.timestamp).getTime()
    )
  })

  const pagination = usePagination(
    {
      sourceData: sortedHistory || [],
      pageSize: 10,
    },
    [sortedHistory],
  )

  return (
    <div className="rounded-lg">
      <Button
        disabled={!history.length}
        className="flex w-full items-center justify-between rounded-lg p-4"
        iconRight={
          <Fa
            icon={isHistoryOpen ? 'chevron-down' : 'chevron-left'}
            size="sm"
          />
        }
        onClick={() => setIsHistoryOpen(!isHistoryOpen)}
      >
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{capitalize(type)} History:</p>
          <span className="text-sm">{history.length || 0} events</span>
        </div>
      </Button>
      {isHistoryOpen && (
        <div className="mt-4 space-y-4">
          {pagination.data?.map(
            (
              {
                eventType,
                pixelCreated,
                pixelUpdated,
                subscriptionCreated,
                subscriptionUpdated,
              },
              index,
            ) => {
              const event =
                pixelCreated ||
                pixelUpdated ||
                subscriptionCreated ||
                subscriptionUpdated
              const rejected = pixelUpdated?.updates?.status === 'denied'
              const approved = pixelUpdated?.updates?.status === 'approved'
              return (
                <div key={`${eventType}_${index}`} className="flex items-start">
                  <p
                    className={classNames('text-base-info text-2xl', {
                      '!text-success-500':
                        eventType === 'PixelUpdated' &&
                        pixelUpdated?.updates?.status === 'approved',
                      '!text-danger-500':
                        eventType === 'PixelUpdated' &&
                        pixelUpdated?.updates?.status === 'denied',
                    })}
                  >
                    •
                  </p>
                  <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full" />
                  <div>
                    <div className="flex items-center gap-2">
                      {event?.userName && (
                        <>
                          <span className="font-medium">{event?.userName}</span>
                          <p>•</p>
                        </>
                      )}
                      <span className="text-sm text-tint-gray-600">
                        {dayjs(event?.timestamp).format(
                          'MM/DD/YYYY, H:mm:ss A',
                        )}
                      </span>
                    </div>
                    <p className="mt-1 text-sm">
                      {(() => {
                        if (rejected) return 'Pixel Rejected'
                        if (approved) return 'Pixel Approved'
                        if (
                          eventType === 'PixelUpdated' ||
                          eventType === 'SubscriptionUpdated'
                        ) {
                          if (
                            eventType === 'SubscriptionUpdated' &&
                            Object.keys(subscriptionUpdated?.updates || {})
                              .length === 0
                          ) {
                            return 'Subscription Updated'
                          }
                          return `${capitalize(type)} Updated: ${Object.entries(
                            pixelUpdated?.updates ||
                              subscriptionUpdated?.updates ||
                              {},
                          )
                            .map(([key, value]) => {
                              if (
                                key === 'tags' ||
                                key === 'states_filtered' ||
                                key === 'zips_filtered'
                              ) {
                                return `${key}: (${(value as string)
                                  .replaceAll(/\[|\]|\(|\)|\{|\}/g, '')
                                  .split(' ')
                                  .join(', ')})`
                              }
                              return `${key} (${value})`
                            })
                            .join(', ')}`
                        }
                        return (eventType || 'UnknownEvent').replace(
                          /([a-z])([A-Z])/g,
                          '$1 $2',
                        )
                      })()}
                    </p>
                  </div>
                </div>
              )
            },
          )}
          {pagination.totalItems > 10 ? (
            <div className="PixelLibraryPage__pagination-footer">
              <TablePagination pagination={pagination} />
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
